import { SmgElement } from '../core/smg-element';
import { html, type SafeHtml } from '../core/safe-html';
import { ssh } from '../services/ssh-service';

/**
 * Gömülü web-SSH paneli (masaüstü): sürüklenebilir, kenardan yeniden
 * boyutlanabilir, "yerleşik ⇄ yüzen" geçişli; konum/boyut localStorage'da.
 * Mobilde alt-sayfa olarak açılır (sürükleme yok).
 *
 * Ayrı bir custom element olması bilinçli: ana görünüm yeniden çizilse bile
 * bu element yerinde kalır → iframe (terminal oturumu) KOPMAZ.
 */
const STORE_KEY = 'smg-ssh-panel';
const MIN_W = 360;
const MIN_H = 240;

interface Geometry {
  x: number;
  y: number;
  w: number;
  h: number;
  /** yerleşik = sağa yapışık tam yükseklik (varsayılan) */
  docked: boolean;
}

function loadGeometry(): Geometry {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return { ...defaultGeometry(), ...(JSON.parse(raw) as Partial<Geometry>) };
  } catch {
    /* yok say */
  }
  return defaultGeometry();
}

function defaultGeometry(): Geometry {
  return { x: 80, y: 90, w: 720, h: 460, docked: true };
}

export class SshPanel extends SmgElement {
  private geo: Geometry = loadGeometry();
  private drag: { dx: number; dy: number } | null = null;
  private resize: { edge: string; sx: number; sy: number; start: Geometry } | null = null;
  private onMove = (ev: PointerEvent) => this.handleMove(ev);
  private onUp = () => this.endGesture();

  private get serial(): string {
    return this.dataset['serial'] ?? '';
  }

  protected override onConnected(): void {
    this.onAct('click', {
      close: () => this.dispatchEvent(new CustomEvent('smg-close', { bubbles: true })),
      toggleDock: () => {
        this.geo = { ...this.geo, docked: !this.geo.docked };
        this.persist();
        this.applyGeometry();
      },
      reset: () => {
        this.geo = defaultGeometry();
        this.persist();
        this.applyGeometry();
      },
    });
    this.onAct('pointerdown', {
      dragHandle: (_el, ev) => this.startDrag(ev as PointerEvent),
      resizeHandle: (el, ev) => this.startResize(el.dataset['edge'] ?? 'se', ev as PointerEvent),
    });
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  protected override onDisconnected(): void {
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
  }

  private persist(): void {
    localStorage.setItem(STORE_KEY, JSON.stringify(this.geo));
  }

  /* ---------------- gestures ---------------- */

  private startDrag(ev: PointerEvent): void {
    if (ev.button !== 0) return;
    // Yerleşikken sürüklemek paneli otomatik "yüzen"e çevirir (kullanıcı beklentisi)
    if (this.geo.docked) {
      const rect = this.getBoundingClientRect();
      this.geo = { ...this.geo, docked: false, x: rect.left, y: rect.top, w: rect.width, h: rect.height };
    }
    this.drag = { dx: ev.clientX - this.geo.x, dy: ev.clientY - this.geo.y };
    this.setPointerCapture?.(ev.pointerId);
    this.classList.add('gesturing');
    ev.preventDefault();
  }

  private startResize(edge: string, ev: PointerEvent): void {
    if (ev.button !== 0) return;
    if (this.geo.docked) {
      const rect = this.getBoundingClientRect();
      this.geo = { ...this.geo, docked: false, x: rect.left, y: rect.top, w: rect.width, h: rect.height };
    }
    this.resize = { edge, sx: ev.clientX, sy: ev.clientY, start: { ...this.geo } };
    this.classList.add('gesturing');
    ev.preventDefault();
    ev.stopPropagation();
  }

  private handleMove(ev: PointerEvent): void {
    if (this.drag) {
      const maxX = window.innerWidth - 80;
      const maxY = window.innerHeight - 40;
      this.geo = {
        ...this.geo,
        x: Math.min(maxX, Math.max(-this.geo.w + 80, ev.clientX - this.drag.dx)),
        y: Math.min(maxY, Math.max(0, ev.clientY - this.drag.dy)),
      };
      this.applyGeometry();
      return;
    }
    if (this.resize) {
      const { edge, sx, sy, start } = this.resize;
      const dx = ev.clientX - sx;
      const dy = ev.clientY - sy;
      let { x, y, w, h } = start;
      if (edge.includes('e')) w = Math.max(MIN_W, start.w + dx);
      if (edge.includes('s')) h = Math.max(MIN_H, start.h + dy);
      if (edge.includes('w')) {
        w = Math.max(MIN_W, start.w - dx);
        x = start.x + (start.w - w);
      }
      if (edge.includes('n')) {
        h = Math.max(MIN_H, start.h - dy);
        y = start.y + (start.h - h);
      }
      this.geo = { ...this.geo, x, y, w, h };
      this.applyGeometry();
    }
  }

  private endGesture(): void {
    if (this.drag || this.resize) {
      this.drag = null;
      this.resize = null;
      this.classList.remove('gesturing');
      this.persist();
    }
  }

  private applyGeometry(): void {
    if (this.geo.docked) {
      this.classList.add('docked');
      this.style.left = this.style.top = this.style.width = this.style.height = '';
    } else {
      this.classList.remove('docked');
      this.style.left = `${this.geo.x}px`;
      this.style.top = `${this.geo.y}px`;
      this.style.width = `${this.geo.w}px`;
      this.style.height = `${this.geo.h}px`;
    }
    const dockBtn = this.qs<HTMLElement>('[data-act="toggleDock"] i');
    if (dockBtn) dockBtn.className = this.geo.docked ? 'ti ti-arrows-diagonal' : 'ti ti-layout-sidebar-right-expand';
    const dockTitle = this.qs<HTMLElement>('[data-act="toggleDock"]');
    if (dockTitle) dockTitle.title = this.geo.docked ? 'Yüzen pencereye çıkar' : 'Sağa yerleştir';
  }

  protected override wire(): void {
    this.applyGeometry();
  }

  protected override template(): SafeHtml {
    const serial = this.serial;
    const url = ssh.termUrl(serial);
    const btn =
      'background:none;border:none;color:var(--vx-term-text-2);cursor:pointer;display:flex;padding:4px;border-radius:6px';
    return html`
      <div class="term-head" data-act="dragHandle" style="cursor:move;user-select:none;touch-action:none">
        <i class="ti ti-grip-vertical" style="font-size:16px;color:var(--vx-term-text-2)"></i>
        <i class="ti ti-terminal-2" style="font-size:18px;color:var(--vx-primary)"></i>
        <span style="font:600 13px var(--vx-font-sans)">web-ssh</span>
        <span class="mono" style="font-size:11px;color:var(--vx-term-text-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0">${serial}</span>
        <span class="chip" style="margin-left:auto;background:rgba(30,158,90,.18);color:var(--vx-term-ok);font-weight:600;flex:none">
          <span style="width:6px;height:6px;border-radius:50%;background:var(--vx-term-ok)"></span>bağlı
        </span>
        <button data-act="reset" title="Konumu sıfırla" style="${btn}"><i class="ti ti-arrow-back-up" style="font-size:16px"></i></button>
        <button data-act="toggleDock" title="Yüzen pencereye çıkar" style="${btn}"><i class="ti ti-arrows-diagonal" style="font-size:17px"></i></button>
        <button data-act="close" title="Kapat (terminalde çıkmak için: exit / Ctrl+D)" style="${btn}"><i class="ti ti-x" style="font-size:19px"></i></button>
      </div>
      <iframe class="term-body" src="${url}" title="web-ssh ${serial}"></iframe>
      <div class="rs rs-n" data-act="resizeHandle" data-edge="n"></div>
      <div class="rs rs-s" data-act="resizeHandle" data-edge="s"></div>
      <div class="rs rs-e" data-act="resizeHandle" data-edge="e"></div>
      <div class="rs rs-w" data-act="resizeHandle" data-edge="w"></div>
      <div class="rs rs-ne" data-act="resizeHandle" data-edge="ne"></div>
      <div class="rs rs-nw" data-act="resizeHandle" data-edge="nw"></div>
      <div class="rs rs-se" data-act="resizeHandle" data-edge="se"></div>
      <div class="rs rs-sw" data-act="resizeHandle" data-edge="sw"></div>`;
  }
}

customElements.define('smg-ssh-panel', SshPanel);
